"use client";

import {
  useState,
  useCallback,
  useRef,
  memo,
  useMemo,
  type RefObject,
} from "react";
import type {
  FacetBucket,
  FacetMatchMode,
  CollectionFacetFilters,
} from "modules/collections/details/model/collection";
import { type FacetDefinition, useFacetDefinitions } from "./facetPresentation";
import { FacetGroup } from "./FacetGroup";
import { FacetSearchableGroup } from "./FacetSearchableGroup";
import { FacetRangeInput } from "./FacetRangeInput";
import { FacetHybridRange } from "./FacetHybridRange";
import { FacetGamePills } from "./FacetGamePills";
import type { FacetSettings } from "utils/facet-settings";
import type { FacetContext, GameId } from "modules/common/model/models";
import { useDefaultGame } from "modules/common/hooks/useDefaultGame";

// Stable empty arrays to avoid `?? []` creating new references every render
const EMPTY_ARRAY: Array<string | number> = [];
const EMPTY_STRING_ARRAY: string[] = [];

interface FacetListProps {
  facets: Record<string, FacetBucket[]>;
  filters: CollectionFacetFilters;
  toggleFacetValue: (
    key: keyof CollectionFacetFilters,
    value: string | number
  ) => void;
  setFacet: <K extends keyof CollectionFacetFilters>(
    key: K,
    value: CollectionFacetFilters[K]
  ) => void;
  setFacets: (updates: Partial<CollectionFacetFilters>) => void;
  settings: FacetSettings;
  currency: string;
  context?: FacetContext;
  gameScope?: GameId;
}

export function FacetList({
  facets,
  filters,
  toggleFacetValue,
  setFacet,
  setFacets,
  settings,
  currency,
  context = "collection",
  gameScope,
}: FacetListProps) {
  const [openSection, setOpenSection] = useState<string | null>(null);

  const toggle = useCallback((section: string) => {
    setOpenSection((prev) => (prev === section ? null : section));
  }, []);

  // Stable ref so FacetItem callbacks can read latest filters without re-rendering
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const hiddenSet = useMemo(() => new Set(settings.hidden), [settings.hidden]);
  const definitions = useFacetDefinitions(context);
  const gameCapability = context === "trade" ? "trading" : "catalogue";
  const { games: userGames, isLoading: gamesLoading } =
    useDefaultGame(gameCapability);
  const userGameSet = useMemo(() => new Set(userGames), [userGames]);
  const gameFacet = definitions.find(
    (definition) => definition.component === "game-pills"
  );
  const gameFilterKey = gameFacet?.filterKey;
  const resultBearingGameBuckets = useMemo(
    () =>
      gameFacet
        ? (facets[gameFacet.facetKey] ?? []).filter(
            (bucket) =>
              bucket.count > 0 && userGameSet.has(String(bucket.value))
          )
        : [],
    [facets, gameFacet, userGameSet]
  );
  const hasMultipleGames = !gamesLoading && resultBearingGameBuckets.length > 1;
  const defMap = useMemo(
    () =>
      new Map(definitions.map((definition) => [definition.key, definition])),
    [definitions]
  );
  const orderedKeys = useMemo(() => {
    const configured = new Set(settings.order);
    return [
      ...settings.order,
      ...definitions
        .map((definition) => definition.key)
        .filter((key) => !configured.has(key)),
    ];
  }, [definitions, settings.order]);

  return (
    <div>
      {orderedKeys.map((key) => {
        if (hiddenSet.has(key)) return null;

        const def = defMap.get(key);
        if (!def) return null;

        const buckets =
          def.component === "game-pills"
            ? resultBearingGameBuckets
            : facets[def.facetKey];

        // Game pills are only useful when multiple preferred games have results.
        if (def.requiresMultipleGames && !hasMultipleGames) return null;

        // Non-game-pills facets: skip if no data AND no active selections
        const activeCount = (
          (filters[def.filterKey] as Array<string | number>) ?? []
        ).length;
        const hasRangeActive =
          (def.minFilterKey && (filters[def.minFilterKey] as number) > 0) ||
          (def.maxFilterKey && (filters[def.maxFilterKey] as number) > 0);
        if (
          !def.requiresMultipleGames &&
          (!buckets || buckets.length === 0) &&
          activeCount === 0 &&
          !hasRangeActive
        )
          return null;

        // Pre-compute per-item values so FacetItem gets stable/primitive props
        const selected =
          (filters[def.filterKey] as Array<string | number>) ?? EMPTY_ARRAY;
        const selectedStrings =
          (filters[def.filterKey] as string[]) ?? EMPTY_STRING_ARRAY;
        const matchMode = def.matchModeKey
          ? ((filters[def.matchModeKey] as FacetMatchMode) ?? "or")
          : undefined;
        const rangeMin = def.minFilterKey
          ? ((filters[def.minFilterKey] as number) ?? 0)
          : 0;
        const rangeMax = def.maxFilterKey
          ? ((filters[def.maxFilterKey] as number) ?? 0)
          : 0;

        return (
          <FacetItem
            key={key}
            def={def}
            buckets={buckets}
            filtersRef={filtersRef}
            selected={selected}
            selectedStrings={selectedStrings}
            matchMode={matchMode}
            rangeMin={rangeMin}
            rangeMax={rangeMax}
            toggleFacetValue={toggleFacetValue}
            setFacet={setFacet}
            setFacets={setFacets}
            sectionKey={key}
            toggle={toggle}
            isOpen={openSection === key}
            currency={currency}
            hasMultipleGames={hasMultipleGames}
            gameFilterKey={gameFilterKey}
            gameScope={gameScope}
          />
        );
      })}
    </div>
  );
}

interface FacetItemProps {
  def: FacetDefinition;
  buckets: FacetBucket[];
  filtersRef: RefObject<CollectionFacetFilters>;
  selected: Array<string | number>;
  selectedStrings: string[];
  matchMode: FacetMatchMode | undefined;
  rangeMin: number;
  rangeMax: number;
  toggleFacetValue: (
    key: keyof CollectionFacetFilters,
    value: string | number
  ) => void;
  setFacet: <K extends keyof CollectionFacetFilters>(
    key: K,
    value: CollectionFacetFilters[K]
  ) => void;
  setFacets: (updates: Partial<CollectionFacetFilters>) => void;
  sectionKey: string;
  toggle: (section: string) => void;
  isOpen: boolean;
  currency: string;
  hasMultipleGames: boolean;
  gameFilterKey?: string;
  gameScope?: GameId;
}

const FacetItem = memo(function FacetItem({
  def,
  buckets,
  filtersRef,
  selected,
  selectedStrings,
  matchMode,
  rangeMin,
  rangeMax,
  toggleFacetValue,
  setFacet,
  setFacets,
  sectionKey,
  toggle,
  isOpen,
  currency,
  hasMultipleGames,
  gameFilterKey,
  gameScope,
}: FacetItemProps) {
  // Stable toggle for accordion open/close
  const onToggleOpen = useCallback(
    () => toggle(sectionKey),
    [toggle, sectionKey]
  );

  // Auto-add the game filter when interacting with a game-exclusive facet.
  const ensureGameFilter = useCallback(() => {
    if (!def.gameExclusive || !hasMultipleGames || !gameFilterKey) return;
    const currentGames = (filtersRef.current[gameFilterKey] as string[]) ?? [];
    if (!currentGames.includes(def.gameExclusive)) {
      toggleFacetValue(gameFilterKey, def.gameExclusive);
    }
  }, [
    def.gameExclusive,
    gameFilterKey,
    hasMultipleGames,
    toggleFacetValue,
    filtersRef,
  ]);

  // For shared facets, auto-add the game filter when selecting a
  // bucket that belongs to a specific game.
  const gameAwareToggle = useCallback(
    (value: string | number, game?: string) => {
      toggleFacetValue(def.filterKey, value);

      if (def.gameExclusive) {
        ensureGameFilter();
        return;
      }

      if (!game) return;

      // Only act when adding, not removing
      const current =
        (filtersRef.current[def.filterKey] as Array<string | number>) ?? [];
      if (current.includes(value)) return;

      if (!gameFilterKey) return;
      const currentGames =
        (filtersRef.current[gameFilterKey] as string[]) ?? [];
      if (!currentGames.includes(game)) {
        toggleFacetValue(gameFilterKey, game);
      }
    },
    [
      def.filterKey,
      def.gameExclusive,
      gameFilterKey,
      ensureGameFilter,
      toggleFacetValue,
      filtersRef,
    ]
  );

  const handleClear = useCallback(() => {
    const updates: Record<string, unknown> = {};
    if (def.component !== "range-input") {
      updates[def.filterKey] = [];
    }
    if (def.matchModeKey) {
      updates[def.matchModeKey] = "or";
    }
    if (def.minFilterKey) {
      updates[def.minFilterKey] = 0;
    }
    if (def.maxFilterKey) {
      updates[def.maxFilterKey] = 0;
    }
    setFacets(updates as Partial<CollectionFacetFilters>);
  }, [def, setFacets]);

  // game-pills onToggle
  const handleGamePillsToggle = useCallback(
    (v: string) => toggleFacetValue(def.filterKey, v),
    [def.filterKey, toggleFacetValue]
  );

  // match mode change (group, searchable-group)
  const handleMatchModeChange = useMemo(
    () =>
      def.matchModeKey
        ? (m: FacetMatchMode) =>
            setFacet(
              def.matchModeKey as keyof CollectionFacetFilters,
              m as CollectionFacetFilters[keyof CollectionFacetFilters]
            )
        : undefined,
    [def.matchModeKey, setFacet]
  );

  // range min change
  const handleMinChange = useCallback(
    (v: number) => {
      ensureGameFilter();
      setFacet(
        def.minFilterKey! as keyof CollectionFacetFilters,
        v as CollectionFacetFilters[keyof CollectionFacetFilters]
      );
    },
    [ensureGameFilter, setFacet, def.minFilterKey]
  );

  // range max change
  const handleMaxChange = useCallback(
    (v: number) => {
      ensureGameFilter();
      setFacet(
        def.maxFilterKey! as keyof CollectionFacetFilters,
        v as CollectionFacetFilters[keyof CollectionFacetFilters]
      );
    },
    [ensureGameFilter, setFacet, def.maxFilterKey]
  );

  // hybrid-range onToggleSpecial
  const handleToggleSpecial = useCallback(
    (v: string) => {
      ensureGameFilter();
      toggleFacetValue(def.filterKey, v);
    },
    [ensureGameFilter, toggleFacetValue, def.filterKey]
  );

  // ── Exclusive value logic (e.g. "C" for colorless) ──
  const { disabledValues, matchModeLocked } = useMemo(() => {
    if (!def.exclusiveValue)
      return { disabledValues: undefined, matchModeLocked: false };

    const excl = def.exclusiveValue;
    const hasExclusive = selected.includes(excl);
    const hasOther = selected.some((v) => v !== excl);

    if (hasExclusive) {
      const disabled = new Set(
        (buckets ?? []).map((b) => b.value).filter((v) => v !== excl)
      );
      return { disabledValues: disabled, matchModeLocked: true };
    }
    if (hasOther) {
      return { disabledValues: new Set([excl]), matchModeLocked: false };
    }
    return { disabledValues: undefined, matchModeLocked: false };
  }, [def.exclusiveValue, selected, buckets]);

  const handleExclusiveToggle = useCallback(
    (value: string | number, game?: string) => {
      if (!def.exclusiveValue) {
        gameAwareToggle(value, game);
        return;
      }

      const excl = def.exclusiveValue;

      if (value === excl) {
        const isSelected = selected.includes(excl);
        if (isSelected) {
          // Toggling C OFF: clear and reset mode
          setFacets({
            [def.filterKey]: [],
            ...(def.matchModeKey ? { [def.matchModeKey]: "or" } : {}),
          } as Partial<CollectionFacetFilters>);
        } else {
          // Toggling C ON: select only C and force exact
          setFacets({
            [def.filterKey]: [excl],
            ...(def.matchModeKey ? { [def.matchModeKey]: "exact" } : {}),
          } as Partial<CollectionFacetFilters>);
          if (def.gameExclusive) ensureGameFilter();
        }
        return;
      }

      // Toggling a real color while C is selected (safety net)
      if (selected.includes(excl)) {
        setFacets({
          [def.filterKey]: [value],
          ...(def.matchModeKey ? { [def.matchModeKey]: "or" } : {}),
        } as Partial<CollectionFacetFilters>);
        if (def.gameExclusive) ensureGameFilter();
        return;
      }

      // Normal toggle
      gameAwareToggle(value, game);
    },
    [def, selected, gameAwareToggle, setFacets, ensureGameFilter]
  );

  // Sort exclusive value (e.g. "C") to the top of the bucket list
  const sortedBuckets = useMemo(() => {
    if (!def.exclusiveValue || !buckets) return buckets;
    const excl = def.exclusiveValue;
    const idx = buckets.findIndex((b) => b.value === excl);
    if (idx <= 0) return buckets;
    return [buckets[idx], ...buckets.slice(0, idx), ...buckets.slice(idx + 1)];
  }, [buckets, def.exclusiveValue]);

  const gameExclusive = def.gameExclusive;

  switch (def.component) {
    case "game-pills":
      return (
        <FacetGamePills
          buckets={buckets}
          selected={selectedStrings}
          onToggle={handleGamePillsToggle}
          onClear={handleClear}
          defaultOpen
        />
      );

    case "group":
      return (
        <FacetGroup
          title={def.title}
          buckets={sortedBuckets}
          selected={selected}
          onToggle={handleExclusiveToggle}
          formatLabel={def.formatLabel}
          renderIcon={def.renderIcon}
          matchMode={matchMode}
          onMatchModeChange={handleMatchModeChange}
          isOpen={isOpen}
          onToggleOpen={onToggleOpen}
          gameExclusive={gameExclusive}
          gameScope={gameScope}
          onClear={handleClear}
          disabledValues={disabledValues}
          matchModeLocked={matchModeLocked}
        />
      );

    case "searchable-group":
      return (
        <FacetSearchableGroup
          title={def.title}
          buckets={buckets}
          selected={selected}
          onToggle={gameAwareToggle}
          matchMode={matchMode}
          onMatchModeChange={handleMatchModeChange}
          isOpen={isOpen}
          onToggleOpen={onToggleOpen}
          gameExclusive={gameExclusive}
          onClear={handleClear}
        />
      );

    case "range-input":
      return (
        <FacetRangeInput
          title={def.title}
          buckets={buckets}
          min={rangeMin}
          max={rangeMax}
          onMinChange={handleMinChange}
          onMaxChange={handleMaxChange}
          step={def.step}
          isOpen={isOpen}
          onToggleOpen={onToggleOpen}
          gameExclusive={gameExclusive}
          currency={def.isCurrency ? currency : undefined}
          onClear={handleClear}
        />
      );

    case "hybrid-range":
      return (
        <FacetHybridRange
          title={def.title}
          buckets={buckets}
          min={rangeMin}
          max={rangeMax}
          onMinChange={handleMinChange}
          onMaxChange={handleMaxChange}
          selectedSpecial={selectedStrings}
          onToggleSpecial={handleToggleSpecial}
          isOpen={isOpen}
          onToggleOpen={onToggleOpen}
          gameExclusive={gameExclusive}
          onClear={handleClear}
        />
      );
  }
});
